import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Card, Badge } from '@/components/ui'
import { useCanManageContracts } from '@/hooks/useCanManageContracts'
import {
  loadContractChargeImpact,
  activateContract,
  createContractTerminationWithRenewal,
  loadContractEditable,
  loadContractPendingTerms,
  setContractFields,
  type ContractChargeImpact,
  type ContractPendingTerm,
} from '@/services/contractService'
import {
  loadAllPartnerLegalEntities,
  loadRoyaltySeriesTerms,
} from '@/services/royaltyService'
import { ContractTermsEditor } from './ContractTermsEditor'
import {
  createEmptyContractTermsForm,
  DEFAULT_FIXED_ROYALTY_BASE_AMOUNT,
  DEFAULT_LISTOK_CRM_BASE_AMOUNT,
  TERM_MODEL_OPTIONS,
  TERM_MODEL_SHORT_LABELS,
  termModelLabel,
  termModelOf,
  termSalesModelPayload,
  type ContractTermsFormData,
} from './ContractTermsFormFields'
import { ContractAmendmentsEditor, type AmendmentPrefill } from './ContractAmendmentsEditor'
import {
  AddPartnerEntityInline,
  EditPartnerEntityInline,
  resolveDefaultEntityCountry,
} from './AddPartnerEntityInline'
import { ROYALTY_CURRENCY_CODES } from '@/lib/currencies'
import type {
  ContractEditable,
  PartnerLegalEntity,
  ContractRenewalStatus,
  ContractAmendmentType,
  ContractLegalStatusCode,
  RoyaltySeriesTerms,
} from '@/types'
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  FileSignature,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  SplitSquareHorizontal,
  X,
} from 'lucide-react'

// Кнопки-действия «оформить изменение по договору» — правильный (событийный) путь правки.
const AMENDMENT_ACTIONS: Array<{
  type: ContractAmendmentType
  label: string
  hint: string
  icon: typeof CalendarClock
  danger?: boolean
}> = [
  {
    type: 'TERM_EXTENSION',
    label: 'Продлить срок',
    hint: 'Сдвигает расчётный конец. Номер и дата договора не меняются.',
    icon: CalendarClock,
  },
  {
    type: 'ENTITY_CHANGE',
    label: 'Сменить юрлицо',
    hint: 'Новая сторона договора с указанной даты. Можно завести юрлицо здесь.',
    icon: Building2,
  },
]

const RESIGN_ACTION = {
  label: 'Расторгнуть + подписать новый',
  hint: 'Создать заготовки ДС и нового ДКК. Они начнут действовать после отметки подписи франчайзи.',
}

const TERMINATION_ACTION = {
  type: 'TERMINATION' as ContractAmendmentType,
  label: 'Расторгнуть',
  hint: 'Дата в ДС = дата фактического завершения договора.',
  icon: Ban,
  danger: true,
}

// Currencies used for the lump-sum (паушальный взнос). Dirham covers Dubai entities.
const LUMP_SUM_CURRENCIES = ['RUB', 'USD', 'EUR', 'AED']

type DerivedLegalStatus = ContractLegalStatusCode

const LEGAL_STATUS_LABELS: Record<DerivedLegalStatus, string> = {
  DRAFT: 'Не активирован: франчайзи не подписал',
  ACTIVE_SIGNED: 'Действует, подписан',
  EXPIRED_RECOGNIZED: 'Истёк, признаётся действующим',
  ENDED: 'Не действует',
  NOT_STARTED: 'Не начался',
}
const LEGAL_STATUS_BADGE: Record<DerivedLegalStatus, 'success' | 'warning' | 'neutral'> = {
  DRAFT: 'warning',
  ACTIVE_SIGNED: 'success',
  EXPIRED_RECOGNIZED: 'warning',
  ENDED: 'neutral',
  NOT_STARTED: 'neutral',
}
const BILLING_END_SOURCE_LABELS: Record<string, string> = {
  ACTUAL_END: 'фактическое завершение',
  TERMINATION: 'расторжение по ДС',
  TERM_EXTENSION: 'продление по ДС',
  EXPECTED_END: 'срок в договоре',
}
const LEGAL_END_BASIS_LABELS: Record<string, string> = {
  ACTUAL_END: 'фактическое завершение',
  RENEWAL_CLOSED_PREVIOUS: 'закрыт при продлении новым ДКК',
  TERMINATION_AMENDMENT: 'расторжение по ДС',
  TERM_EXTENSION_AMENDMENT: 'продление по ДС',
  EXPECTED_END: 'срок в договоре',
  OPEN_ENDED: 'без указанного срока',
}
const inputCls = 'w-full text-xs border border-border rounded px-2 py-1.5 bg-background disabled:opacity-60'
const contractErrorAlertCls =
  'flex items-start gap-2 rounded-md border-2 border-destructive bg-destructive/15 px-3 py-2.5 text-sm font-medium text-destructive shadow-sm'

function ContractErrorAlert({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div role="alert" aria-live="assertive" className={`${contractErrorAlertCls} ${className}`}>
      <AlertTriangle size={16} className="mt-0.5 flex-none" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

function toDateInputValue(value: string | null | undefined): string {
  if (!value) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function formatDate(value: string | null | undefined): string {
  const iso = toDateInputValue(value)
  if (!iso) return '—'
  const [year, month, day] = iso.split('-')
  return `${day}.${month}.${year}`
}

function dateInputDisplay(value: string): string {
  if (!value) return ''
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : value
}

function parseDateInputDisplay(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoMatch) return trimmed
  const ruMatch = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/)
  if (!ruMatch) return trimmed
  const [, day, month, year] = ruMatch
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

function isValidIsoDate(value: string): boolean {
  if (!value) return true
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  // Несуществующая дата (31.02, 13-й месяц) формат проходит, но Date её сдвигает или не разбирает.
  const d = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value
}

function addDaysIso(iso: string, days: number): string {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return ''
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function termsToForm(terms: RoyaltySeriesTerms): ContractTermsFormData {
  return {
    contract_type: terms.contract_type ?? 'REGULAR',
    sales_model: terms.sales_model === 'MIXED' ? 'MIXED' : 'SUBSCRIPTION',
    effective_from: terms.effective_from?.slice(0, 10) ?? '',
    effective_to: terms.effective_to?.slice(0, 10) ?? '',
    royalty_percent: String(terms.royalty_percent ?? ''),
    agency_percent: terms.agency_percent != null ? String(terms.agency_percent) : '1',
    vat_percent: terms.vat_percent != null ? String(terms.vat_percent) : '22',
    listok_crm_base_amount: terms.listok_crm_base_amount != null
      ? String(terms.listok_crm_base_amount)
      : terms.contract_type === 'SUBSCRIPTION' ? DEFAULT_LISTOK_CRM_BASE_AMOUNT : DEFAULT_FIXED_ROYALTY_BASE_AMOUNT,
    is_listok_crm_carrier: terms.is_listok_crm_carrier === true,
    is_fixed_royalty_carrier: terms.is_fixed_royalty_carrier === true,
    min_royalty_amount: String(terms.min_royalty_amount ?? ''),
    currency_code: terms.currency_code ?? 'RUB',
    min_royalty_currency: '',
    source_document_note: terms.source_document_note ?? '',
    notes: terms.notes ?? '',
  }
}

type FormState = {
  contract_number: string
  signed_at: string
  effective_from: string
  expected_end_date: string
  actual_end_date: string
  entity_id: string
  lump_sum_amount: string
  lump_sum_currency: string
  levita_entity: string
  renewal_status: ContractRenewalStatus | ''
}

function toForm(d: ContractEditable): FormState {
  return {
    contract_number: d.contract_number ?? '',
    signed_at: toDateInputValue(d.signed_at),
    effective_from: toDateInputValue(d.effective_from),
    expected_end_date: toDateInputValue(d.expected_end_date),
    actual_end_date: toDateInputValue(d.actual_end_date),
    entity_id: d.entity_id != null ? String(d.entity_id) : '',
    lump_sum_amount: d.lump_sum_amount != null ? String(d.lump_sum_amount) : '',
    lump_sum_currency: d.lump_sum_currency ?? '',
    levita_entity: d.levita_entity ?? '',
    renewal_status: d.renewal_status ?? '',
  }
}

// Поля договора, у которых есть событийный (ДС) аналог. Прямая правка допустима только как
// исправление ошибки в оригинале; реальное изменение по подписанному ДС должно стать событием
// (иначе теряется история/сегментация — тот же анти-паттерн, что и дубль MAIN).
type MonitoredField = 'entity_id' | 'expected_end_date' | 'actual_end_date'
const MONITORED_FIELDS: Array<{
  field: MonitoredField
  label: string
  type: ContractAmendmentType
  cta: string
}> = [
  { field: 'entity_id', label: 'Юрлицо франчайзи', type: 'ENTITY_CHANGE', cta: 'Оформить смену юрлица' },
  { field: 'expected_end_date', label: 'Ожид. окончание', type: 'TERM_EXTENSION', cta: 'Оформить продление срока' },
  { field: 'actual_end_date', label: 'Факт. окончание', type: 'TERMINATION', cta: 'Оформить расторжение' },
]

/**
 * Shared editor for a contract (ДКК) as the primary entity: number, dates, legal entity,
 * lump sum, LEVITA entity, renewal status and royalty terms. Used both on the royalty contract card and the
 * documents-registry detail. Writes go through one advanced-gated RPC (set_contract_fields);
 * non-advanced users see a read-only form. See docs/ROYALTY_CONTRACT_LEVEL_ACCRUAL_PLAN.md → UI-1.
 */
export function ContractEditor({
  contractId,
  onSaved,
}: {
  contractId: number
  onSaved?: () => void
}) {
  // ⚠️ Право на договоры с 01.09.2026 — «юрист или супер-админ», а не «advanced УК»
  // (миграция 20260901140000). Спрашиваем сервер той же функцией, что стоит в гейтах
  // RPC: getAccessLevel кэширует уровень в localStorage без TTL, и после смены прав
  // человек ещё долго видел бы старую картину.
  const { canManage: canEdit } = useCanManageContracts()
  const [data, setData] = useState<ContractEditable | null>(null)
  const [entities, setEntities] = useState<PartnerLegalEntity[]>([])
  const [form, setForm] = useState<FormState | null>(null)
  // Юрлицо, выбранное в пикере стороны: под ним показывается правка его реквизитов
  // (незаполненный налоговый номер, название-заглушка).
  const selectedEntity = useMemo(
    () => entities.find(e => String(e.entity_id) === String(form?.entity_id ?? '')) ?? null,
    [entities, form?.entity_id]
  )
  const [chargeImpact, setChargeImpact] = useState<ContractChargeImpact | null>(null)
  const [activeEntityId, setActiveEntityId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [guardOpen, setGuardOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [resignOpen, setResignOpen] = useState(false)
  // Активация — операция, а не поле формы, поэтому у неё своё состояние и своя модалка.
  const [activationOpen, setActivationOpen] = useState(false)
  const [activationDate, setActivationDate] = useState('')
  const [activationBusy, setActivationBusy] = useState(false)
  const [prefill, setPrefill] = useState<AmendmentPrefill | null>(null)
  // Условия заготовки ДКК («Расторгнуть и подписать новый»): встанут при отметке подписи.
  const [pendingTerms, setPendingTerms] = useState<ContractPendingTerm[]>([])
  const seqRef = useRef(0)

  const draftLoaded = data != null && data.activated_at == null
  useEffect(() => {
    if (!canEdit || !draftLoaded) {
      setPendingTerms([])
      return
    }
    let cancelled = false
    loadContractPendingTerms(contractId)
      .then(rows => {
        if (!cancelled) setPendingTerms(rows)
      })
      .catch(() => {
        if (!cancelled) setPendingTerms([])
      })
    return () => {
      cancelled = true
    }
  }, [canEdit, contractId, draftLoaded])

  const load = useCallback(() => {
    setLoading(true)
    loadContractEditable(contractId)
      .then(async d => {
        setData(d)
        if (d) {
          setForm(toForm(d))
          setActiveEntityId(d.entity_id)
          const [all, impact] = await Promise.all([
            loadAllPartnerLegalEntities(),
            loadContractChargeImpact(contractId).catch(() => null),
          ])
          setEntities(all.filter(e => e.partner_number === d.partner_number))
          setChargeImpact(impact)
          setError(null)
        } else {
          setForm(null)
          setChargeImpact(null)
          setActiveEntityId(null)
          setError('Договор не найден')
        }
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [contractId])

  useEffect(() => {
    load()
  }, [load])

  function setField<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm(f => (f ? { ...f, [field]: value } : f))
  }

  // Открыть форму ДС нужного типа (пустую) — из крупных кнопок-действий над карточкой.
  function startAmendment(type: ContractAmendmentType) {
    seqRef.current += 1
    setPrefill({ seq: seqRef.current, amendment_type: type })
  }

  function handleResigned(_newContractId: number) {
    setResignOpen(false)
    onSaved?.()
    load()
  }

  const entityLabel = (id: number | null): string => {
    if (id == null) return '—'
    const e = entities.find(x => x.entity_id === id)
    return e ? `${e.legal_name}${e.inn ? ` · ИНН ${e.inn}` : ''}` : `Юрлицо #${id}`
  }

  const fmtDate = formatDate

  const baseline = data ? toForm(data) : null
  const hasChanges = !!form && !!baseline && JSON.stringify(form) !== JSON.stringify(baseline)
  const protectedPeriods = chargeImpact?.protectedPeriods ?? []
  const protectedPeriodLabels = protectedPeriods.map(period =>
    `${String(period.month).padStart(2, '0')}.${period.year}`
  )

  const protectedPeriodOverlaps = (from: string | null, to: string | null): boolean =>
    protectedPeriods.some(period => {
      const monthStart = `${period.year}-${String(period.month).padStart(2, '0')}-01`
      const monthEnd = new Date(Date.UTC(period.year, period.month, 0)).toISOString().slice(0, 10)
      return monthEnd >= (from || '0001-01-01') && monthStart <= (to || '9999-12-31')
    })

  // Заполнение ПУСТОЙ стороны договора структурным изменением не является: границы
  // contract_party_intervals считаются из дат договора и ДС, entity_id там payload,
  // поэтому покрытие месяца, сегментация и суммы начислений не меняются. Сервер это
  // разрешает с миграции 20260907140000 (замена уже указанного лица и снятие стороны
  // по-прежнему блокируются) — фронт обязан зеркалить, иначе кнопка «Сохранить»
  // остаётся серой там, где база пропускает.
  const isEmptyPartyFill = !!form && !!baseline && !baseline.entity_id && !!form.entity_id

  const hasProtectedStructureChanges = (() => {
    if (!form || !baseline || protectedPeriods.length === 0) return false
    const nonBoundaryStructureChanged = (
      form.signed_at !== baseline.signed_at
      || form.effective_from !== baseline.effective_from
      || (form.entity_id !== baseline.entity_id && !isEmptyPartyFill)
      || form.renewal_status !== baseline.renewal_status
    )
    if (nonBoundaryStructureChanged) {
      return protectedPeriodOverlaps(
        [form.effective_from, baseline.effective_from, form.signed_at, baseline.signed_at].filter(Boolean).sort()[0] || null,
        null,
      )
    }
    if (form.expected_end_date === baseline.expected_end_date && form.actual_end_date === baseline.actual_end_date) {
      return false
    }
    // Правка только документального срока покрытие не меняет: его закрывают фактическое
    // окончание и ДС о расторжении, истёкший срок — «признаётся действующим». Сервер её
    // пропускает с миграции 20261021150000 — зеркалим, иначе «Сохранить» серая зря.
    if (form.actual_end_date === baseline.actual_end_date) return false
    const oldEnd = baseline.actual_end_date || baseline.expected_end_date || null
    const newEnd = form.actual_end_date || form.expected_end_date || null
    const finiteEnds = [oldEnd, newEnd].filter((value): value is string => Boolean(value)).sort()
    if (finiteEnds.length === 0) return false
    // Пока дата набирается, в форме лежит недописанный текст («12.10.2»): граница не
    // вычисляется, а сохранение и так закрыто проверкой формата дат.
    const boundary = addDaysIso(finiteEnds[0], 1)
    if (!boundary) return false
    return protectedPeriodOverlaps(
      boundary,
      oldEnd && newEnd ? [oldEnd, newEnd].sort()[1] : null,
    )
  })()

  // Какие «событийные» поля изменены относительно сохранённого договора.
  // Первичное указание стороны — не смена юрлица по ДС: менять было нечего. Модалку
  // «Изменение по ДС?» на него не показываем, иначе юрист обязан выбрать между двумя
  // неверными ответами — «оформить ДС» (события не было) и «это исправление ошибки».
  const changedMonitored =
    form && baseline
      ? MONITORED_FIELDS.filter(
          m => form[m.field] !== baseline![m.field] && !(m.field === 'entity_id' && isEmptyPartyFill)
        )
      : []

  const todayIso = new Date().toISOString().slice(0, 10)
  const statusFromForm: DerivedLegalStatus = form
    ? form.actual_end_date && todayIso > form.actual_end_date
      ? 'ENDED'
      : form.expected_end_date && todayIso > form.expected_end_date
        ? 'EXPIRED_RECOGNIZED'
        : form.effective_from && todayIso < form.effective_from
          ? 'NOT_STARTED'
          : 'ACTIVE_SIGNED'
    : 'NOT_STARTED'
  // Заготовки нет в contract_legal_state_v, поэтому серверный статус у неё пустой:
  // состояние читается по самой отметке активации.
  const isActivated = data?.activated_at != null
  const useSavedLegalState = !!data?.legal_status_code && !hasChanges
  // У заготовки серверного статуса нет вовсе (её нет в contract_legal_state_v),
  // поэтому без этой ветки вывод по датам показал бы её действующим договором.
  const derivedStatus: DerivedLegalStatus = !isActivated
    ? 'DRAFT'
    : useSavedLegalState
      ? data.legal_status_code!
      : statusFromForm
  const legalCoverageEndDate =
    useSavedLegalState
      ? data.legal_end_date || ''
      : form?.actual_end_date ||
        (derivedStatus === 'EXPIRED_RECOGNIZED' ? todayIso : form?.expected_end_date || '')
  const displayedExpectedEndDate = useSavedLegalState
    ? data.legal_current_expected_end_date ?? data.billing_end_date ?? form?.expected_end_date ?? ''
    : form?.expected_end_date ?? ''
  const displayedActualEndDate = useSavedLegalState
    ? data.actual_end_date || (data.legal_end_basis === 'TERMINATION_AMENDMENT' ? data.legal_end_date ?? '' : '')
    : form?.actual_end_date ?? ''
  const coverageSourceLabel = useSavedLegalState && data.legal_end_basis
    ? LEGAL_END_BASIS_LABELS[data.legal_end_basis] ?? data.legal_end_basis
    : data?.billing_end_source
      ? BILLING_END_SOURCE_LABELS[data.billing_end_source] ?? data.billing_end_source
      : ''
  const legalTitle = useSavedLegalState && data.legal_status_label
    ? data.legal_status_label
    : LEGAL_STATUS_LABELS[derivedStatus]
  const legalBasis = useSavedLegalState && data.legal_status_reason
    ? `Основание: ${data.legal_status_reason}.`
    : derivedStatus === 'ACTIVE_SIGNED'
      ? 'Основание: подписанный договор, документальный срок не истёк.'
      : derivedStatus === 'EXPIRED_RECOGNIZED'
        ? 'Основание: документальный срок истёк, но фактическое окончание не указано.'
        : derivedStatus === 'ENDED'
          ? 'Основание: указана дата фактического окончания.'
          : 'Основание: дата начала действия ещё не наступила.'
  const coverageText = form
    ? derivedStatus === 'ENDED'
        ? legalCoverageEndDate
          ? `Не действует после ${fmtDate(legalCoverageEndDate)}${coverageSourceLabel ? ` (${coverageSourceLabel})` : ''}.`
          : 'Дата фактического завершения не указана.'
        : derivedStatus === 'EXPIRED_RECOGNIZED'
          ? 'Действует до фактического закрытия.'
        : derivedStatus === 'NOT_STARTED'
          ? `Начнёт действовать ${fmtDate(form.effective_from)}.`
            : legalCoverageEndDate
              ? `Действует до ${fmtDate(legalCoverageEndDate)}${coverageSourceLabel ? ` (${coverageSourceLabel})` : ''}.`
              : 'Действует без указанной даты окончания.'
    : ''

  // Перенаправить правку поля в ДС: откатить поле к оригиналу и открыть форму события с предзаполнением.
  function openAmendment(m: (typeof MONITORED_FIELDS)[number]) {
    if (!form || !baseline) return
    seqRef.current += 1
    const seq = seqRef.current
    let draft: AmendmentPrefill
    if (m.type === 'ENTITY_CHANGE') {
      draft = { seq, amendment_type: 'ENTITY_CHANGE', new_entity_id: form.entity_id || undefined }
    } else if (m.type === 'TERM_EXTENSION') {
      draft = { seq, amendment_type: 'TERM_EXTENSION', new_expected_end_date: form.expected_end_date || undefined }
    } else {
      // Расторжение: effective_from в ДС хранит саму дату фактического окончания.
      draft = {
        seq,
        amendment_type: 'TERMINATION',
        effective_from: form.actual_end_date || undefined,
      }
    }
    // Оригинал остаётся как есть — источником нового состояния становится событие.
    setField(m.field, baseline[m.field])
    setPrefill(draft)
    setGuardOpen(false)
  }

  async function handleSave(asCorrection = false) {
    if (!form) return
    if (hasProtectedStructureChanges) {
      setError(
        `Изменение затрагивает зафиксированный период роялти (${protectedPeriodLabels.join(', ')}). `
        + 'Сначала отмените выставление роялти и снимите фиксацию выручки. После изменения зафиксируйте выручку и выставьте роялти заново.'
      )
      return
    }
    // Правка событийного поля напрямую = либо исправление ошибки, либо забытое ДС. Не сохраняем
    // молча — заставляем выбрать намерение (продолжает описанный анти-паттерн правки оригинала).
    if (!asCorrection && changedMonitored.length > 0) {
      setGuardOpen(true)
      return
    }
    // contracts_lump_sum_currency_check: сумма и валюта пауша — обе заданы или обе пусты.
    const hasAmount = form.lump_sum_amount.trim() !== ''
    const hasCurrency = form.lump_sum_currency !== ''
    if (hasAmount !== hasCurrency) {
      setError('Паушальный взнос: укажите и сумму, и валюту (или очистите оба поля).')
      return
    }
    if (
      !isValidIsoDate(form.signed_at) ||
      !isValidIsoDate(form.effective_from) ||
      !isValidIsoDate(form.expected_end_date) ||
      !isValidIsoDate(form.actual_end_date)
    ) {
      setError('Введите даты в формате дд.мм.гггг.')
      return
    }
    setGuardOpen(false)
    setSaving(true)
    setError(null)
    try {
      await setContractFields(contractId, {
        contract_number: form.contract_number.trim() || null,
        signed_at: form.signed_at || null,
        effective_from: form.effective_from || null,
        expected_end_date: form.expected_end_date || null,
        actual_end_date: form.actual_end_date || null,
        entity_id: form.entity_id ? parseInt(form.entity_id) : null,
        lump_sum_amount: form.lump_sum_amount.trim() ? Number(form.lump_sum_amount) : null,
        lump_sum_currency: form.lump_sum_currency || null,
        levita_entity: form.levita_entity.trim() || null,
        renewal_status: form.renewal_status || null,
      })
      onSaved?.()
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  // Контекстная подсказка под изменённым событийным полем — предлагает оформить ДС.
  function amendmentHint(field: MonitoredField) {
    if (!canEdit || !form || !baseline || form[field] === baseline[field]) return null
    const m = MONITORED_FIELDS.find(x => x.field === field)!
    return (
      <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px]">
        <span className="inline-flex items-center gap-1 text-warning">
          <AlertTriangle size={10} /> Изменение по подписанному ДС?
        </span>
        <button
          type="button"
          onClick={() => openAmendment(m)}
          className="inline-flex items-center gap-0.5 font-medium text-primary underline hover:opacity-80"
        >
          {m.cta} <ArrowRight size={10} />
        </button>
        <span className="text-muted-foreground">Прямая правка — только исправление ошибки.</span>
      </div>
    )
  }

  if (loading) {
    return <Card className="p-3 mb-4 text-xs text-muted-foreground">Загрузка договора…</Card>
  }

  if (!form || !data) {
    return (
      <Card className="p-3 mb-4 border-2 border-destructive bg-destructive/5">
        <ContractErrorAlert>{error ?? 'Договор не найден'}</ContractErrorAlert>
      </Card>
    )
  }

  return (
    <Card className="p-3 mb-4">
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="min-w-0">
          <div className="text-base font-semibold truncate">
            {form.contract_number || `Договор #${contractId}`}
          </div>
        </div>
      </div>

      {error && (
        <ContractErrorAlert className="mb-3 text-xs">{error}</ContractErrorAlert>
      )}

      {/* Правильный (событийный) путь изменения — самый заметный */}
      {canEdit && (
        <div className="mb-3">
          <div className="mb-3 rounded-lg border border-border p-2.5">
            <div className="text-xs font-semibold">Подпись франчайзи</div>
            <p className="text-[11px] text-muted-foreground mb-2">
              {isActivated
                ? 'Договор активирован и принят к учёту.'
                : 'Пока франчайзи не подписал, договор в системе не действует: роялти по нему не начисляется, а предыдущий договор серии остаётся открытым.'}
            </p>
            {!isActivated && pendingTerms.length > 0 && (
              <div className="mb-2 rounded-md bg-muted/50 px-2.5 py-2 text-[11px]">
                <div className="font-medium">Условия роялти, которые встанут при подписании</div>
                <ul className="mt-1 space-y-0.5 text-muted-foreground">
                  {pendingTerms.map(term => (
                    <li key={term.pending_term_id}>
                      {fmtDate(term.effective_from)} — {term.effective_to ? fmtDate(term.effective_to) : 'бессрочно'}:{' '}
                      {termModelLabel(term)}, {term.royalty_percent}%,
                      минимум {term.min_royalty_amount.toLocaleString('ru-RU')} {term.currency_code}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!isActivated && (
              <button
                type="button"
                onClick={() => {
                  setActivationDate(form.signed_at || todayIso)
                  setActivationOpen(true)
                }}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
              >
                Франчайзи подписал
              </button>
            )}
          </div>

          <div className="text-xs font-semibold">Оформить изменение по договору</div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Так меняется договор по документам — история сохраняется, роялти считается верно.
          </p>
          <div className="grid gap-1.5 sm:grid-cols-4">
            {AMENDMENT_ACTIONS.map(a => {
              const Icon = a.icon
              return (
                <button
                  key={a.type}
                  type="button"
                  onClick={() => startAmendment(a.type)}
                  className="flex items-start gap-2 text-left rounded-lg border border-border p-2.5 hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <span
                    className={`flex-none grid place-items-center w-8 h-8 rounded-md ${
                      a.danger ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                    }`}
                  >
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-medium">{a.label}</span>
                    <span className="block text-[10px] text-muted-foreground leading-snug">{a.hint}</span>
                  </span>
                </button>
              )
            })}
            <button
              type="button"
              onClick={() => setResignOpen(true)}
              className="flex items-start gap-2 text-left rounded-lg border border-border p-2.5 hover:border-primary hover:bg-primary/5 transition-colors"
            >
              <span className="flex-none grid place-items-center w-8 h-8 rounded-md bg-destructive/10 text-destructive">
                <SplitSquareHorizontal size={16} />
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium">{RESIGN_ACTION.label}</span>
                <span className="block text-[10px] text-muted-foreground leading-snug">{RESIGN_ACTION.hint}</span>
              </span>
            </button>
            {(() => {
              const Icon = TERMINATION_ACTION.icon
              return (
                <button
                  type="button"
                  onClick={() => startAmendment(TERMINATION_ACTION.type)}
                  className="flex items-start gap-2 text-left rounded-lg border border-border p-2.5 hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <span className="flex-none grid place-items-center w-8 h-8 rounded-md bg-destructive/10 text-destructive">
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-medium">{TERMINATION_ACTION.label}</span>
                    <span className="block text-[10px] text-muted-foreground leading-snug">{TERMINATION_ACTION.hint}</span>
                  </span>
                </button>
              )
            })()}
          </div>
        </div>
      )}

      {/* Правовой статус вычисляется из дат: подписан / истёк, но признаётся / не действует. */}
      <div className="mb-2 rounded-lg border border-border p-2.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              <ShieldCheck size={13} className={derivedStatus === 'ENDED' ? 'text-muted-foreground' : 'text-success'} />
              Правовой статус договора
            </div>
            <div className="mt-1 text-lg font-semibold leading-tight">{legalTitle}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{legalBasis}</div>
            <div className="mt-1 text-xs font-medium">{coverageText}</div>
          </div>
          <Badge variant={LEGAL_STATUS_BADGE[derivedStatus]} className="text-[11px] px-2 py-0.5">
            {LEGAL_STATUS_LABELS[derivedStatus]}
          </Badge>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-md bg-muted/30 p-2 text-xs sm:grid-cols-4">
          <SummaryItem label="Подписан" value={fmtDate(form.signed_at)} />
          <SummaryItem
            label="Франчайзи подписал"
            value={isActivated ? fmtDate(data.activated_at?.slice(0, 10) ?? null) : 'нет'}
          />
          <SummaryItem label="Начало действия" value={fmtDate(form.effective_from)} />
          <SummaryItem label="Документальный срок" value={fmtDate(displayedExpectedEndDate)} />
          <SummaryItem label="Факт. завершение" value={fmtDate(displayedActualEndDate)} />
        </div>

        {!isActivated && (
          <div className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs">
            <div className="font-semibold text-warning">
              Договор заведён, но не активирован: франчайзи ещё не подписал.
            </div>
            <div className="mt-1 text-muted-foreground">
              Пока он не активирован, роялти по нему не начисляется, предыдущий договор серии
              не закрывается, а сам договор не виден расчёту и юридическим отчётам.
            </div>
          </div>
        )}

        {derivedStatus === 'EXPIRED_RECOGNIZED' && (
          <div className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs">
            <div className="font-semibold text-warning">
              Документальный срок истёк {fmtDate(displayedExpectedEndDate)}, но фактическое окончание не указано.
            </div>
            <div className="mt-1 text-muted-foreground">
              До фиксации фактического окончания договор считается признаваемым действующим.
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {canEdit && (
                <>
                  <button
                    type="button"
                    onClick={() => startAmendment('TERM_EXTENSION')}
                    className="rounded-md bg-primary px-2 py-1 text-[11px] text-primary-foreground"
                  >
                    Оформить продление ДС
                  </button>
                  <button
                    type="button"
                    onClick={() => startAmendment('TERMINATION')}
                    className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-muted"
                  >
                    Оформить завершение
                  </button>
                </>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Данные договора — по умолчанию только для чтения */}
      <div className="rounded-lg border border-border bg-muted/30 p-2.5">
        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
          Данные договора
        </div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs sm:grid-cols-3">
          <SummaryItem label="Номер" value={form.contract_number || '—'} />
          <SummaryItem label="Подписан" value={fmtDate(form.signed_at)} />
          <SummaryItem
            label="Франчайзи подписал"
            value={
              isActivated
                ? [fmtDate(data.activated_at?.slice(0, 10) ?? null), data.activated_by_name]
                    .filter(Boolean)
                    .join(' · ')
                : 'нет'
            }
          />
          <SummaryItem label="Начало действия" value={fmtDate(form.effective_from)} />
          <SummaryItem label="Документальный срок" value={fmtDate(displayedExpectedEndDate)} />
          <SummaryItem label="Факт. завершение" value={fmtDate(displayedActualEndDate)} />
          <SummaryItem
            label="Паушальный взнос"
            value={
              form.lump_sum_amount
                ? `${form.lump_sum_amount}${form.lump_sum_currency ? ` ${form.lump_sum_currency}` : ''}`
                : '—'
            }
          />
          <SummaryItem label="Юр лицо LEVITA" value={form.levita_entity || '—'} />
          <SummaryItem
            className="col-span-2 sm:col-span-3"
            label="Текущая сторона договора"
            value={entityLabel(activeEntityId)}
          />
          {activeEntityId !== data.entity_id && (
            <SummaryItem
              className="col-span-2 sm:col-span-3"
              label="Сторона при подписании"
              value={entityLabel(data.entity_id)}
            />
          )}
        </dl>
      </div>

      {/* Правка оригинала — за замком, только исправление опечаток */}
      {canEdit && (
        <div className="mt-2 rounded-lg border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => setEditOpen(o => !o)}
            className="w-full flex items-center gap-2 p-2.5 text-left"
          >
            <span className="flex-none grid place-items-center w-7 h-7 rounded-md bg-warning/15 text-warning">
              <Pencil size={14} />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold">Исправить данные договора</span>
              <span className="block text-[10px] text-muted-foreground">
                Только опечатки в оригинале. Реальные изменения — через ДС выше.
              </span>
            </span>
            <ChevronRight
              size={16}
              className={`ml-auto flex-none text-muted-foreground transition-transform ${editOpen ? 'rotate-90' : ''}`}
            />
          </button>

          {editOpen && (
            <div className="border-t border-border p-2.5 space-y-2">
              <div className="flex gap-2 rounded-md bg-warning/10 text-warning px-2.5 py-2 text-[11px]">
                <AlertTriangle size={14} className="flex-none mt-0.5" />
                <span>
                  Перезапишет подписанный оригинал и не пересчитает историю. Срок, юрлицо и расторжение
                  оформляйте как ДС — кнопками выше.
                </span>
              </div>

              {hasChanges && chargeImpact && chargeImpact.total > 0 && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                  По этому договору уже есть начисления: {chargeImpact.total}
                  {chargeImpact.confirmed > 0 ? `, из них зафиксировано ${chargeImpact.confirmed}` : ''}.
                  Сохранение изменит мастер-данные договора, но не пересчитает историю автоматически.
                </div>
              )}

              {protectedPeriods.length > 0 && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
                  Договор участвует в зафиксированных периодах: {protectedPeriodLabels.join(', ')}.
                  Изменения, затрагивающие эти даты, будут заблокированы и в интерфейсе, и в базе.
                  Для исправления сначала отмените роялти и снимите фиксацию выручки.
                </div>
              )}

              {protectedPeriods.length > 0 && isEmptyPartyFill && (
                <div className="rounded-md border border-primary/40 bg-primary/10 px-2.5 py-2 text-xs">
                  Юрлицо у договора не было указано вовсе, поэтому его можно заполнить и при
                  зафиксированных периодах: суммы начислений и расчётные интервалы от этого не
                  меняются — меняется только то, на кого выставляется счёт.
                </div>
              )}

              <div>
                <label className="text-[10px] text-muted-foreground">Номер договора</label>
                <input
                  type="text"
                  value={form.contract_number}
                  onChange={e => setField('contract_number', e.target.value)}
                  placeholder="Например: 618-Н"
                  className={inputCls}
                />
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div>
                  <label className="text-[10px] text-muted-foreground">Подписан</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={dateInputDisplay(form.signed_at)}
                    onChange={e =>
                      setForm(f =>
                        f
                          ? {
                              ...f,
                              signed_at: parseDateInputDisplay(e.target.value),
                              effective_from: f.effective_from || parseDateInputDisplay(e.target.value),
                            }
                          : f
                      )
                    }
                    placeholder="дд.мм.гггг"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Начало действия</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={dateInputDisplay(form.effective_from)}
                    onChange={e => setField('effective_from', parseDateInputDisplay(e.target.value))}
                    placeholder="дд.мм.гггг"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Документальный срок</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={dateInputDisplay(form.expected_end_date)}
                    onChange={e => setField('expected_end_date', parseDateInputDisplay(e.target.value))}
                    placeholder="дд.мм.гггг"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Факт. завершение</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={dateInputDisplay(form.actual_end_date)}
                    onChange={e => setField('actual_end_date', parseDateInputDisplay(e.target.value))}
                    placeholder="дд.мм.гггг"
                    className={inputCls}
                  />
                </div>
              </div>
              {amendmentHint('expected_end_date')}
              {amendmentHint('actual_end_date')}
              {form.actual_end_date && form.actual_end_date !== baseline.actual_end_date && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                  При сохранении фактического завершения условия роялти по этому договору будут закрыты с {fmtDate(form.actual_end_date)}.
                  Если дата попадает в зафиксированный период роялти, база отклонит изменение.
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground">Паушальный взнос</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.lump_sum_amount}
                    onChange={e => setField('lump_sum_amount', e.target.value)}
                    placeholder="Сумма"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Валюта пауша</label>
                  <select
                    value={form.lump_sum_currency}
                    onChange={e => setField('lump_sum_currency', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">—</option>
                    {LUMP_SUM_CURRENCIES.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label className="text-[10px] text-muted-foreground">Сторона при подписании (оригинал)</label>
                  <select
                    value={form.entity_id}
                    onChange={e => setField('entity_id', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— не выбрано —</option>
                    {entities.map(e => (
                      <option key={e.entity_id} value={String(e.entity_id)}>
                        {e.legal_name} · ИНН {e.inn ?? '—'}
                      </option>
                    ))}
                  </select>
                  <AddPartnerEntityInline
                    partnerNumber={data.partner_number}
                    defaultCountry={resolveDefaultEntityCountry(entities)}
                    onCreated={e => {
                      setEntities(prev =>
                        prev.some(x => x.entity_id === e.entity_id) ? prev : [...prev, e]
                      )
                      setField('entity_id', String(e.entity_id))
                    }}
                  />
                  {selectedEntity && (
                    <EditPartnerEntityInline
                      entity={selectedEntity}
                      onUpdated={e =>
                        setEntities(prev =>
                          prev.map(x => (x.entity_id === e.entity_id ? e : x))
                        )
                      }
                    />
                  )}
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Юр лицо LEVITA</label>
                  <input
                    type="text"
                    value={form.levita_entity}
                    onChange={e => setField('levita_entity', e.target.value)}
                    placeholder="РФ / Дубай / …"
                    className={inputCls}
                  />
                </div>
              </div>
              {amendmentHint('entity_id')}

              <div className="flex justify-end pt-1">
                <button
                  onClick={() => handleSave()}
                  disabled={saving || !hasChanges || hasProtectedStructureChanges}
                  title={hasProtectedStructureChanges ? 'Изменение затрагивает зафиксированный период роялти' : undefined}
                  className="flex items-center gap-1 text-xs bg-primary text-primary-foreground px-2.5 py-1.5 rounded-lg disabled:opacity-50"
                >
                  {saving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />}
                  Сохранить
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!canEdit && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          Изменение договоров и оформление ДС доступно только юристам и супер-администраторам.
        </div>
      )}

      <ContractTermsEditor
        seriesId={data.series_id}
        contractId={contractId}
        contractNumber={form.contract_number || null}
        canEdit={canEdit}
        chargeImpact={chargeImpact}
        coverageStartDate={form.effective_from || form.signed_at || null}
        coverageEndDate={legalCoverageEndDate || null}
        contractIsEnded={derivedStatus === 'ENDED'}
        onChanged={onSaved}
      />

      {activationOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-4 shadow-lg">
            <div className="text-sm font-semibold">
              Франчайзи подписал договор
            </div>

            <>
                <p className="mt-2 text-xs text-muted-foreground">
                  Договор станет действующим с {fmtDate(form.effective_from)}. Предыдущий договор
                  серии будет закрыт днём раньше, его условия роялти закроются той же датой
                  {pendingTerms.length > 0
                    ? ', а у этого договора встанут условия, заданные при его создании.'
                    : ' и перейдут на этот договор.'}{' '}
                  Если вместе с ним оформлен ДС о расторжении предыдущего, сначала отметьте
                  подписание ДС — иначе при выставленном месяце расторжения база откажет. Если
                  период уже выставлен, границы пересекаются с другим действующим ДКК или между
                  договорами остаётся день без покрытия, база отклонит операцию.
                </p>
                <div className="mt-3 rounded-md bg-muted/50 px-2.5 py-2 text-xs">
                  <div className="text-[10px] text-muted-foreground">Начало действия договора</div>
                  <div className="font-medium">{fmtDate(form.effective_from)}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    Эта дата задана в черновике и при подписании не меняется.
                  </div>
                </div>
                <label className="mt-3 block text-[10px] text-muted-foreground">Дата подписания документа</label>
                <input
                  type="date"
                  value={activationDate}
                  onChange={e => setActivationDate(e.target.value)}
                  required
                  className={inputCls}
                />
            </>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setActivationOpen(false)}
                disabled={activationBusy}
                className="rounded-md border border-border px-3 py-1.5 text-xs"
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={
                  activationBusy ||
                  !activationDate
                }
                onClick={async () => {
                  setActivationBusy(true)
                  try {
                    await activateContract(contractId, activationDate || null)
                    setActivationOpen(false)
                    onSaved?.()
                    load()
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e))
                    setActivationOpen(false)
                  } finally {
                    setActivationBusy(false)
                  }
                }}
                className="rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
              >
                {activationBusy ? 'Сохраняем…' : 'Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ContractAmendmentsEditor
        contractId={contractId}
        entities={entities}
        baseEntityId={data.entity_id}
        partnerNumber={data.partner_number}
        canEdit={canEdit}
        protectedPeriodLabels={protectedPeriodLabels}
        prefill={prefill}
        onActiveEntityChange={setActiveEntityId}
        onEntityCreated={e =>
          setEntities(prev => (prev.some(x => x.entity_id === e.entity_id) ? prev : [...prev, e]))
        }
        onChanged={onSaved}
      />

      {resignOpen && (
        <TerminateAndResignModal
          contract={data}
          onClose={() => setResignOpen(false)}
          onCreated={handleResigned}
        />
      )}

      {guardOpen && baseline && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          onClick={() => setGuardOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-background rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-1">
              <FileSignature size={16} className="text-primary" />
              <div className="text-base font-semibold">Изменение по документу или исправление?</div>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Вы поменяли поля, у которых есть событийный аналог. Если изменение произошло по
              подписанному <span className="font-medium">дополнительному соглашению</span> — оформите
              его как ДС, чтобы сохранить историю договора и корректно считать роялти. Прямую правку
              оригинала используйте только чтобы исправить ошибку ввода.
            </p>

            <div className="space-y-1.5 mb-3">
              {changedMonitored.map(m => (
                <div
                  key={m.field}
                  className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5"
                >
                  <div className="text-xs min-w-0">
                    <div className="font-medium">{m.label}</div>
                    <div className="text-muted-foreground truncate">
                      {baseline[m.field] || '—'} → <span className="text-foreground">{form[m.field] || '—'}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openAmendment(m)}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-2.5 py-1.5 rounded-lg"
                  >
                    {m.cta} <ArrowRight size={12} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
              <button
                type="button"
                onClick={() => setGuardOpen(false)}
                className="text-xs text-muted-foreground"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={hasProtectedStructureChanges}
                title={hasProtectedStructureChanges ? 'Исправление затрагивает зафиксированный период роялти' : undefined}
                className="text-xs border border-border px-3 py-1.5 rounded-lg hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                Это исправление ошибки — сохранить в оригинале
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

function TerminateAndResignModal({
  contract,
  onClose,
  onCreated,
}: {
  contract: ContractEditable
  onClose: () => void
  onCreated: (contractId: number) => void
}) {
  const [terminationDate, setTerminationDate] = useState('')
  const [note, setNote] = useState('')
  const [docUrl, setDocUrl] = useState('')
  const [contractNumber, setContractNumber] = useState('')
  const [signedAt, setSignedAt] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [expectedEndDate, setExpectedEndDate] = useState('')
  const [termsForms, setTermsForms] = useState<ContractTermsFormData[]>(() => [createEmptyContractTermsForm()])
  const [loadingTerms, setLoadingTerms] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // setState становится видимым только после следующего рендера. Без ref два
  // быстрых клика успевают параллельно создать два ДС, прежде чем кнопка станет disabled.
  const saveInFlightRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    setLoadingTerms(true)
    loadRoyaltySeriesTerms(contract.series_id)
      .then(rows => {
        if (cancelled) return
        const latest = rows
          .filter(row => row.contract_id === contract.contract_id)
          .sort((a, b) => b.effective_from.localeCompare(a.effective_from) || b.term_id - a.term_id)[0] ?? null
        if (latest) {
          setTermsForms(current => [{
            ...termsToForm(latest),
            effective_from: current[0]?.effective_from ?? '',
            effective_to: '',
            source_document_note: '',
            notes: '',
          }])
        }
        setError(null)
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoadingTerms(false)
      })
    return () => {
      cancelled = true
    }
  }, [contract.contract_id, contract.series_id])

  useEffect(() => {
    const nextStart = terminationDate ? addDaysIso(terminationDate, 1) : ''
    setEffectiveFrom(nextStart)
    setTermsForms(current => current.map((term, index, rows) => ({
      ...term,
      effective_from: index === 0 ? nextStart : term.effective_from,
      effective_to: index === rows.length - 1 ? '' : term.effective_to,
    })))
  }, [terminationDate])

  function setTermField<K extends keyof ContractTermsFormData>(
    index: number,
    field: K,
    value: ContractTermsFormData[K]
  ) {
    setTermsForms(current =>
      current.map((term, termIndex) => {
        if (termIndex !== index) return termIndex === current.length - 1 ? { ...term, effective_to: '' } : term
        if (field === 'contract_type' && value !== term.contract_type) {
          const previousDefault = term.contract_type === 'SUBSCRIPTION' ? DEFAULT_LISTOK_CRM_BASE_AMOUNT : DEFAULT_FIXED_ROYALTY_BASE_AMOUNT
          const nextDefault = value === 'SUBSCRIPTION' ? DEFAULT_LISTOK_CRM_BASE_AMOUNT : DEFAULT_FIXED_ROYALTY_BASE_AMOUNT
          return {
            ...term,
            contract_type: value as ContractTermsFormData['contract_type'],
            listok_crm_base_amount: term.listok_crm_base_amount === previousDefault ? nextDefault : term.listok_crm_base_amount,
          }
        }
        return {
          ...term,
          [field]: field === 'effective_to' && termIndex === current.length - 1 ? '' : value,
        }
      })
    )
  }

  function addTermsInterval() {
    setTermsForms(current => {
      const last = current[current.length - 1] ?? createEmptyContractTermsForm()
      const nextStart = last.effective_to ? addDaysIso(last.effective_to, 1) : ''
      return [
        ...current.map((term, index) => (index === current.length - 1 ? term : term)),
        {
          ...last,
          effective_from: nextStart,
          effective_to: '',
          source_document_note: '',
          notes: '',
        },
      ]
    })
  }

  function removeTermsInterval(index: number) {
    setTermsForms(current => {
      if (current.length <= 1) return current
      const next = current.filter((_, termIndex) => termIndex !== index)
      return next.map((term, termIndex) => (termIndex === next.length - 1 ? { ...term, effective_to: '' } : term))
    })
  }

  const validationError = useMemo(() => {
    const dateValues = [
      terminationDate,
      signedAt,
      effectiveFrom,
      expectedEndDate,
      ...termsForms.flatMap(term => [term.effective_from, term.effective_to]),
    ]
    if (!dateValues.every(isValidIsoDate)) return 'Введите даты в формате дд.мм.гггг.'
    if (!terminationDate) return 'Укажите дату расторжения.'
    if (!contractNumber.trim()) return 'Укажите номер нового договора.'
    if (!effectiveFrom) return 'Укажите начало действия нового договора.'
    if (effectiveFrom <= terminationDate) {
      return 'Новый договор должен начинаться после даты расторжения старого, чтобы договоры не пересекались.'
    }
    if (expectedEndDate && expectedEndDate < effectiveFrom) {
      return 'Документальный срок нового договора не может быть раньше начала действия.'
    }
    for (let index = 0; index < termsForms.length; index += 1) {
      const term = termsForms[index]
      if (!term.effective_from) return `Укажите начало действия условий #${index + 1}.`
      if (term.effective_from < effectiveFrom) {
        return `Условия #${index + 1} не могут начинаться раньше самого договора.`
      }
      if (index < termsForms.length - 1 && !term.effective_to) {
        return `Укажите дату окончания условий #${index + 1}. Последний интервал остаётся бессрочным.`
      }
      if (index === termsForms.length - 1 && term.effective_to) {
        return 'Последний интервал условий должен быть бессрочным.'
      }
      if (term.effective_to && term.effective_to < term.effective_from) {
        return `Дата окончания условий #${index + 1} не может быть раньше даты начала.`
      }
      const nextTerm = termsForms[index + 1]
      if (nextTerm && term.effective_to && addDaysIso(term.effective_to, 1) > nextTerm.effective_from) {
        return `Условия #${index + 1} пересекаются со следующей редакцией.`
      }
      if (!term.royalty_percent.trim()) return `Укажите ставку роялти в условиях #${index + 1}.`
      if (!term.min_royalty_amount.trim()) return `Укажите минимальное роялти в условиях #${index + 1}.`
      if (!Number.isFinite(Number(term.royalty_percent)) || !Number.isFinite(Number(term.min_royalty_amount))) {
        return `Ставка роялти и минимальное роялти в условиях #${index + 1} должны быть числами.`
      }
      if (
        term.contract_type === 'SUBSCRIPTION' &&
        (!Number.isFinite(Number(term.agency_percent)) ||
          !Number.isFinite(Number(term.vat_percent)) ||
          !Number.isFinite(Number(term.listok_crm_base_amount)))
      ) {
        return `Ставки агентского и НДС, а также сумма ручного агентского в условиях #${index + 1} должны быть числами.`
      }
    }
    return null
  }, [contractNumber, effectiveFrom, expectedEndDate, signedAt, terminationDate, termsForms])

  const canSubmit = !saving && !loadingTerms && !validationError

  async function handleSave() {
    if (saveInFlightRef.current) return
    if (validationError) {
      setError(validationError)
      return
    }

    saveInFlightRef.current = true
    setSaving(true)
    setError(null)
    try {
      // Один серверный вызов: ДС, ДКК и условия либо заводятся вместе, либо не
      // заводится ничего. Условия лежат при заготовке и встают при подписании ДКК.
      const newContractId = await createContractTerminationWithRenewal({
        sourceContractId: contract.contract_id,
        terminationDate,
        terminationNote: note.trim() || null,
        terminationDocUrl: docUrl.trim() || null,
        contractNumber,
        signedAt: signedAt || null,
        effectiveFrom,
        expectedEndDate: expectedEndDate || null,
        terms: termsForms.map((term, index) => {
          const isSubscription = term.contract_type === 'SUBSCRIPTION'
          return {
            contract_type: term.contract_type,
            sales_model: termSalesModelPayload(term),
            effective_from: term.effective_from,
            effective_to: index === termsForms.length - 1 ? null : term.effective_to || null,
            royalty_percent: parseFloat(term.royalty_percent),
            agency_percent: isSubscription ? parseFloat(term.agency_percent || '0') : null,
            vat_percent: isSubscription ? parseFloat(term.vat_percent || '0') : null,
            // Обычному терму база нужна только под фиксированную часть роялти.
            listok_crm_base_amount: isSubscription
              ? parseFloat(term.listok_crm_base_amount || '0')
            : term.is_fixed_royalty_carrier ? parseFloat(term.listok_crm_base_amount || DEFAULT_FIXED_ROYALTY_BASE_AMOUNT) : null,
            is_listok_crm_carrier: isSubscription ? term.is_listok_crm_carrier : false,
            is_fixed_royalty_carrier: !isSubscription ? term.is_fixed_royalty_carrier : false,
            min_royalty_amount: parseFloat(term.min_royalty_amount || '0'),
            currency_code: term.currency_code,
            source_document_note: term.source_document_note.trim() || null,
            notes: term.notes.trim() || null,
          }
        }),
      })

      onCreated(newContractId)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      saveInFlightRef.current = false
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-lg bg-background p-4 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-base font-semibold">Расторгнуть и подписать новый договор</div>
            <p className="text-[11px] text-muted-foreground">
              Старый договор: {contract.contract_number || `#${contract.contract_id}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={15} />
          </button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
            <AlertTriangle size={12} className="mt-0.5 flex-none" />
            <span>{error}</span>
          </div>
        )}

        <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs text-muted-foreground">
          Будут созданы две заготовки: ДС о расторжении и новый ДКК с условиями ниже. Пока
          франчайзи не подпишет каждый документ и вы не отметите это в его карточке, текущий
          договор и его условия роялти останутся действующими. Отмечать подписание: сначала ДС о
          расторжении, затем новый ДКК — при подписи ДКК его условия встанут в роялти.
        </div>

        <div className="space-y-4">
          <section>
            <div className="mb-2 text-sm font-semibold">ДС о расторжении</div>
            <div className="grid gap-2 sm:grid-cols-3">
              <div>
                <label className="text-[10px] text-muted-foreground">Дата расторжения</label>
                <input type="date" value={terminationDate} onChange={e => setTerminationDate(e.target.value)} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] text-muted-foreground">Ссылка на скан ДС</label>
                <input type="url" value={docUrl} onChange={e => setDocUrl(e.target.value)} placeholder="https://..." className={inputCls} />
              </div>
            </div>
            <div className="mt-2">
              <label className="text-[10px] text-muted-foreground">Комментарий</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Основание / примечание" className={inputCls} />
            </div>
          </section>

          <section>
            <div className="mb-2 text-sm font-semibold">Новый договор</div>
            <div className="grid gap-2 sm:grid-cols-4">
              <div>
                <label className="text-[10px] text-muted-foreground">Номер</label>
                <input type="text" value={contractNumber} onChange={e => setContractNumber(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Дата подписания (если известна)</label>
                <input type="date" value={signedAt} onChange={e => setSignedAt(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Начало действия</label>
                <input
                  type="date"
                  value={effectiveFrom}
                  onChange={e => {
                    setEffectiveFrom(e.target.value)
                    setTermsForms(current =>
                      current.map((term, index, rows) => ({
                        ...term,
                        effective_from: index === 0 ? e.target.value : term.effective_from,
                        effective_to: index === rows.length - 1 ? '' : term.effective_to,
                      }))
                    )
                  }}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Документальный срок</label>
                <input type="date" value={expectedEndDate} onChange={e => setExpectedEndDate(e.target.value)} className={inputCls} />
              </div>
            </div>
          </section>

          <section>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">Условия нового договора</div>
                <p className="text-[10px] text-muted-foreground">До активации нового ДКК на роялти не влияют.</p>
              </div>
              <button
                type="button"
                onClick={addTermsInterval}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
              >
                <Plus size={12} /> Добавить условия
              </button>
            </div>
            {loadingTerms ? (
              <div className="text-xs text-muted-foreground">Загрузка условий...</div>
            ) : (
              <div className="space-y-3">
                {termsForms.map((term, index) => (
                  <ResignTermsForm
                    key={index}
                    form={term}
                    index={index}
                    isLast={index === termsForms.length - 1}
                    canRemove={termsForms.length > 1}
                    onChange={(field, value) => setTermField(index, field, value)}
                    onRemove={() => removeTermsInterval(index)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
          <button type="button" onClick={onClose} className="text-xs text-muted-foreground">
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
          >
            {saving ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
            Создать заготовки
          </button>
        </div>
      </div>
    </div>
  )
}

function ResignTermsForm({
  form,
  index,
  isLast,
  canRemove,
  onChange,
  onRemove,
}: {
  form: ContractTermsFormData
  index: number
  isLast: boolean
  canRemove: boolean
  onChange: <K extends keyof ContractTermsFormData>(field: K, value: ContractTermsFormData[K]) => void
  onRemove: () => void
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-xs font-semibold">Условия #{index + 1}</div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5 text-xs">
            {TERM_MODEL_OPTIONS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                title={label}
                onClick={() => {
                  onChange('contract_type', value === 'REGULAR' ? 'REGULAR' : 'SUBSCRIPTION')
                  if (value !== 'REGULAR') onChange('sales_model', value)
                }}
                className={`rounded px-2.5 py-1 ${
                  termModelOf(form) === value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {TERM_MODEL_SHORT_LABELS[value]}
              </button>
            ))}
          </div>
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={`Удалить условия #${index + 1}`}
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="text-[10px] text-muted-foreground">Условия действуют с</label>
          <input
            type="date"
            value={form.effective_from}
            onChange={e => onChange('effective_from', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Действуют по</label>
          {isLast ? (
            <div className={`${inputCls} flex items-center text-muted-foreground`}>Бессрочно</div>
          ) : (
            <input
              type="date"
              value={form.effective_to}
              onChange={e => onChange('effective_to', e.target.value)}
              className={inputCls}
            />
          )}
        </div>
      </div>

      <div className="mt-2 grid gap-2 sm:grid-cols-4">
        <div>
          <label className="text-[10px] text-muted-foreground">Ставка, %</label>
          <input
            type="number"
            step="0.1"
            value={form.royalty_percent}
            onChange={e => onChange('royalty_percent', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Валюта роялти</label>
          <select
            value={form.currency_code}
            onChange={e => {
              onChange('currency_code', e.target.value)
              onChange('min_royalty_currency', '')
            }}
            className={inputCls}
          >
            {ROYALTY_CURRENCY_CODES.map(currency => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Минималка</label>
          <input
            type="number"
            value={form.min_royalty_amount}
            onChange={e => onChange('min_royalty_amount', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Валюта минимума</label>
          <div className={`${inputCls} flex items-center text-muted-foreground`}>{form.currency_code}</div>
        </div>
      </div>

      {form.contract_type === 'SUBSCRIPTION' && (
        <div className="mt-2 rounded-md bg-blue-50 p-2 space-y-2 dark:bg-blue-950/30">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-blue-700 dark:text-blue-400">Агентское, %</label>
              <input
                type="number"
                step="0.01"
                value={form.agency_percent}
                onChange={e => onChange('agency_percent', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-[10px] text-blue-700 dark:text-blue-400">НДС, %</label>
              <input
                type="number"
                step="1"
                value={form.vat_percent}
                onChange={e => onChange('vat_percent', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-[10px] text-blue-700 dark:text-blue-400">Ручное агентское, ₽</label>
              <input
                type="number"
                step="1"
                value={form.listok_crm_base_amount}
                onChange={e => onChange('listok_crm_base_amount', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.is_listok_crm_carrier}
              onChange={e => onChange('is_listok_crm_carrier', e.target.checked)}
            />
            <span>Начислять ручное агентское с этого договора</span>
          </label>
        </div>
      )}
      {form.contract_type === 'REGULAR' && (
        <div className="mt-2 space-y-2">
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.is_fixed_royalty_carrier}
              disabled={form.currency_code !== 'RUB' && !form.is_fixed_royalty_carrier}
              onChange={e => onChange('is_fixed_royalty_carrier', e.target.checked)}
            />
            <span>
              Начислять фиксированную часть роялти с этого договора
              <span className="block text-[10px]">
                {form.currency_code === 'RUB'
                  ? 'Ежемесячно, без НДС; сумму месяца можно изменить корректировкой при выставлении.'
                  : 'Только для договоров с роялти в рублях.'}
              </span>
            </span>
          </label>
          {form.is_fixed_royalty_carrier && (
            <div>
              <label className="text-[10px] text-muted-foreground">Фиксированная часть, ₽ в месяц (без НДС)</label>
              <input
                type="number"
                step="1"
                value={form.listok_crm_base_amount}
                onChange={e => onChange('listok_crm_base_amount', e.target.value)}
                className={inputCls}
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div>
          <label className="text-[10px] text-muted-foreground">Документ-основание</label>
          <input
            type="text"
            placeholder="Например: новый ДКК"
            value={form.source_document_note}
            onChange={e => onChange('source_document_note', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Комментарий</label>
          <input
            type="text"
            placeholder="Примечание к условиям"
            value={form.notes}
            onChange={e => onChange('notes', e.target.value)}
            className={inputCls}
          />
        </div>
      </div>
    </div>
  )
}

// Одна пара «подпись → значение» в read-only-сводке данных договора.
function SummaryItem({
  label,
  value,
  className,
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={className}>
      <dt className="text-[10px] text-muted-foreground">{label}</dt>
      <dd className="font-medium truncate" title={value}>
        {value}
      </dd>
    </div>
  )
}
